import { Body, Controller, Get, Param, Post, Query, Req, Res, UploadedFiles, UseInterceptors } from '@nestjs/common';
import { errorHandler } from '@gtpl/shared-utils/error-messages';
import { ApiConsumes, ApiHeader, ApiOperation, ApiParam, ApiQuery, ApiTags } from '@nestjs/swagger';
import { AllPostsResponseModel, PointsResponseModel, PostResponseModel, SingleResponseModel } from './dto/post-response-model';
import { GSearchDto, Pagination1Dto, Pagination2Dto, PaginationDto, PercentDto, PinPostDto, PostApprvDto, PostDeleteDto, PostDTO } from './dto/post.dto';
import { PostService } from './post.service';
import { FilesInterceptor } from '@nestjs/platform-express';
import { imageFileFilter } from '../shared/file-upload';
import { diskStorage } from 'multer';
import { extname } from 'path';
import { ErrorResponse } from '@gtpl/shared-models/common-models';

@ApiTags('Post')
@Controller('post')
export class PostController {
    constructor (
        private postService: PostService,
    ){}

    /**
     * create text/url post 
     * @param queryParam which returns api version
     * @param postDto which passes details to be saved
     * @param request which returns headers 
     * @returns postResponse which returns the updated data along with status (true/false), errortype, intlcode, internal message which provides message the calling function.   
     */
    @ApiHeader({
        name:'Authorization',
        description:'Authorization token'
    })  
    @Post('/createPost')
    @ApiOperation({summary:'Post Types: 1 - text/url, 2 - Image, 3 - Video, 4 - Poll: user for postTypeId value'})  
    @ApiQuery({name:'apiVersion'})  
    @UseInterceptors(
		FilesInterceptor('attachments', 5, {
			//fileFilter: imageFileFilter,
			storage: diskStorage({
				destination: './public/postFiles',
				filename: function (req, file, callback) {
					const name = file.originalname.split('.')[0];
					const fileExtName = extname(file.originalname);
					const randomName = Array(4)
						.fill(null)
						.map(() => Math.round(Math.random() * 16).toString(16))
						.join('');
					callback(null, `${name}-${randomName}${fileExtName}`);
				}
			}),			
		}),
    )
    @ApiConsumes('multipart/form-data')
    async createPost(@Query() queryParam,@UploadedFiles() attachments,@Body() postDto:PostDTO,@Req() request:Request): Promise<PostResponseModel> {
        try {
            if(postDto.postTypeId == 1) { //text/url post
                return await this.postService.createPost(queryParam,postDto,request);
            } else {
                const postData = await this.postService.createPostWithReturnResult(queryParam, postDto);
                if(postData) {
                    if(postDto.postTypeId == 2) {
                        const pointsData = {
                            postId:postData.Id,
                            userId:postDto.userId,
                            actionBy:postDto.userId,
                            points:4,
                            activity:'Image Post',
                            versionFlag:queryParam.apiVersion != null ? queryParam.apiVersion : 1
                        }
                        await this.postService.createPostPoints(pointsData);
                    } else if(postDto.postTypeId == 3) {
                        const pointsData = {
                            postId:postData.Id,
                            userId:postDto.userId,
                            actionBy:postDto.userId,
                            points:5,
                            activity:'Video Post',
                            versionFlag:queryParam.apiVersion != null ? queryParam.apiVersion : 1
                        }
                        await this.postService.createPostPoints(pointsData);
                    } else {
                        const pointsData = {
                            postId:postData.Id,
                            userId:postDto.userId,
                            actionBy:postDto.userId,
                            points:7,
                            activity:'Poll Post',
                            versionFlag:queryParam.apiVersion != null ? queryParam.apiVersion : 1
                        }
                        await this.postService.createPostPoints(pointsData);
                    }
                    if(postDto.postTypeId == 2 || postDto.postTypeId == 3) { // 2 image post, 3 video post
                        const response = [];
                        attachments.forEach(file => {
                            const fileReponse = {
                                postId:postData.Id,
                                userId:postDto.userId,
                                fileType: file.originalname.match(/\.(jpg|jpeg|png|gif)$/) ? 'image' : 'video',
                                fileUrl: `post/postFiles/${file.filename}`,
                                createdUser:postDto.createdUser,
                                versionFlag:postDto.versionFlag
                            };
                            response.push(fileReponse);
                        })
                        return await this.postService.createPostAttachments(queryParam, response, postData.Id);
                    } else {
                        var dataToPost = postDto.pollOptions.map((el) => {
                            return {
                                postId:postData.Id,
                                userId:postDto.userId,
                                pollType: 'text',
                                pollValue: el,
                                createdUser:postDto.userId,
                                versionFlag:postDto.versionFlag
                            };
                        });
                        return await this.postService.createPostPoll(queryParam, dataToPost,postData.Id);
                    }
                } else {
                    throw new ErrorResponse(11106, 'Post Not Saved')
                }
            }
        } catch (error) {
            return errorHandler(PostResponseModel, error);
        }
    }

    /**
     * update text/url post
     * @param queryParams which returns api version
     * @param postDto which passes details to be updated
     * @param request which returns headers 
     * @returns postResponse which returns the updated data along with status (true/false), errortype, intlcode, internal message which provides message the calling function.   
     */
    @ApiHeader({
        name:'Authorization',
        description:'Authorization token'
    })  
    @Post('/editPost')
    @ApiOperation({summary:'Post Types: 1 - text/url, 2 - Image, 3 - Video, 4 - Poll: user for postTypeId value'})  
    @ApiQuery({name:'apiVersion'})  
    @UseInterceptors(
		FilesInterceptor('attachments', 5, {
			//fileFilter: imageFileFilter,
			storage: diskStorage({
				destination: './public/postFiles',
				filename: function (req, file, callback) {
					const name = file.originalname.split('.')[0];
					const fileExtName = extname(file.originalname);
					const randomName = Array(4)
						.fill(null)
						.map(() => Math.round(Math.random() * 16).toString(16))
						.join('');
					callback(null, `${name}-${randomName}${fileExtName}`);
				}
			}),			
		}),
    )
    @ApiConsumes('multipart/form-data')
    async editPost(@Query() queryParam,@UploadedFiles() attachments,@Body() postDto:PostDTO,@Req() request:Request): Promise<PostResponseModel> {
        try {
            const postData = await this.postService.editPostWithReturnResult(queryParam, postDto);
            if(postDto.versionFlag) {
                postDto.versionFlag = postDto.versionFlag
            } else {
                postDto.versionFlag = 1
            }
            if(postData) {
                const response = [];
                if(attachments) {
                    attachments.forEach(file => {
                        const fileReponse = {
                            postId:postDto.Id,
                            userId:postDto.userId,
                            fileType: file.originalname.match(/\.(jpg|jpeg|png|gif)$/) ? 'image' : 'video',
                            fileUrl: `post/postFiles/${file.filename}`,
                            createdUser:postDto.userId,
                            versionFlag:postDto.versionFlag 
                        };
                        response.push(fileReponse);
                    })
                }
                if(postDto.pollOptions) {
                    postDto.pollOptions = postDto.pollOptions                    
                }  else {
                    postDto.pollOptions = [];
                }                   
                return await this.postService.editPostAttachments(queryParam, response, postDto.pollOptions, postDto.Id,postDto.userId,postDto.versionFlag);
            } else {
                throw new ErrorResponse(11106, 'Post Not Saved')
            }
        } catch (error) {
            return errorHandler(PostResponseModel, error);
        }
    }

    @ApiHeader({
        name:'Authorization',
        description:'Authorization token'
    })  
    @Get('/getActivePosts/:userId') //home, hot,fev,dev,user
    @ApiOperation({summary:'get post list - query param type means is it a pinned post = 0, home posts = 1 , hot posts = 2, favourite posts=3, development posts =4, user created posts for profile =5'})
    @ApiParam({name:'userId'})
    @ApiQuery({name:'apiVersion'})
    @ApiQuery({name:'skip'})
    @ApiQuery({name:'type'})
    @ApiQuery({name:'villageId'})
    @ApiQuery({name:'filterId'})
    async getActivePosts(@Query() pageDto:PaginationDto, @Param('userId') userId, @Req() request:Request):Promise<AllPostsResponseModel> {
        try {
            return await this.postService.getActivePosts(pageDto, userId,request);
        } catch (error) {
            return errorHandler(AllPostsResponseModel, error);
        }
    }

    @ApiHeader({
        name:'Authorization',
        description:'Authorization token'
    })  
    @Get('/getNewPosts') //home, hot,fev,dev,user
    @ApiOperation({summary:'type: 2 - new posts, 3 for disapproved'})
    @ApiQuery({name:'apiVersion'})
    @ApiQuery({name:'skip'})
    @ApiQuery({name:'type'})
    async getNewPosts(@Query() pageDto:Pagination2Dto, @Req() request:Request):Promise<AllPostsResponseModel> {
        try {
            return await this.postService.getNewPosts(pageDto,request);
        } catch (error) {
            return errorHandler(AllPostsResponseModel, error);
        }
    }

    @ApiHeader({
        name:'Authorization',
        description:'Authorization token'
    })  
    @Post('/postApproval')
    @ApiOperation({summary:'1 - for approval and 3 - for decline'})
    @ApiQuery({name:'apiVersion'})
    async postApproval(@Query() queryParams,@Body() postApprvDto:PostApprvDto,@Req() request:Request): Promise<SingleResponseModel> {
        try {
            return await this.postService.postApproval(queryParams,postApprvDto,request);
        } catch (error) {
            return errorHandler(SingleResponseModel, error);
        }
    }
    

    @ApiHeader({
        name:'Authorization',
        description:'Authorization token'
    })  
    @Get('/getLatestRefreshedPosts/:userId') //posts refresh
    @ApiOperation({summary:'get post list - refreshed latest posts'})
    @ApiParam({name:'userId'})
    @ApiQuery({name:'apiVersion'})
    @ApiQuery({name:'skip'})
    @ApiQuery({name:'lastPostId'})
    async getLatestRefreshedPosts(@Query() pageDto:Pagination1Dto, @Param('userId') userId, @Req() request:Request):Promise<AllPostsResponseModel> {
        try {
            return await this.postService.getLatestRefreshedPosts(pageDto, userId,request);
        } catch (error) {
            return errorHandler(AllPostsResponseModel, error);
        }
    }

    @ApiHeader({
        name:'Authorization',
        description:'Authorization token'
    })
    @Get('/getPostById/:postId/:userId')
    @ApiQuery({name:'apiVersion'})
    @ApiParam({name:'postId'})
    @ApiParam({name:'userId'})
    async getPostById(@Query() queryParams, @Param('postId') postId, @Param('userId') userId, @Req() request:Request):Promise<SingleResponseModel> {
        try {
            return await this.postService.getPostById(queryParams, postId,userId,request);
        } catch (error) {
            return errorHandler(SingleResponseModel, error);
        }
    }

    @Get('/postFiles/:imgpath')
    @ApiParam({name:'imgpath'})
    seeUploadedFile(@Param('imgpath') Image, @Res() res) {
		try {
			return res.sendFile(Image, { root: './public/postFiles' });
		} catch (error) {
			return error.message;
		}
	}

    @ApiHeader({
        name:'Authorization',
        description:'Authorization token'
    })  
    @Post('/pinPost')
    @ApiQuery({name:'apiVersion'})
    async pinPost(@Query() queryParams,@Body() pinPostDto:PinPostDto,@Req() request:Request): Promise<SingleResponseModel> {
        try {
            return await this.postService.pinPost(queryParams,pinPostDto,request);
        } catch (error) {
            return errorHandler(SingleResponseModel, error);
        }
    }

    @Get('/leaderBoard')
    @ApiQuery({name:'apiVersion'})
    async leaderBoard(@Query() queryParams,@Req() request:Request):Promise<PointsResponseModel> {
		try {
            return await this.postService.leaderBoard(queryParams,request);
        } catch (error) {
            return errorHandler(PointsResponseModel, error);
        }
	} 

    @ApiHeader({
        name:'Authorization',
        description:'Authorization token'
    })  
    @Post('/globalSearch')
    @ApiQuery({name:'apiVersion'})
    async globalSearch(@Query() queryParams,@Body() gSearchDto: GSearchDto,@Req() request:Request): Promise<SingleResponseModel> {
        try {
            return await this.postService.globalSearch(queryParams,gSearchDto,request);
        } catch (error) {
            return errorHandler(SingleResponseModel, error);
        }
    }

    @ApiHeader({
        name:'Authorization',
        description:'Authorization token'
    })
    @Post('/updatePostStatus')
    @ApiOperation({summary:'for post soft delete'})
    @ApiQuery({name:'apiVersion'})
    async updatePostStatus(@Query() queryParams, @Body() PostDeleteDto:PostDeleteDto, @Req() request: Request): Promise<SingleResponseModel> {
        try {
            return await this.postService.updatePostStatus(queryParams, PostDeleteDto, request);
        } catch (error) {
            return errorHandler(SingleResponseModel, error);
        }
    }

    /* @ApiHeader({
        name:'Authorization',
        description:'Authorization token'
    })  
    @Post('/developmentPercent')
    @ApiQuery({name:'apiVersion'})
    async developmentPercent(@Query() queryParams,@Body() percentDto: PercentDto,@Req() request:Request): Promise<SingleResponseModel> {
        try {
            if(queryParams.apiVersion == 1) {
                return await this.postService.developmentPercent(percentDto,request);
            } else {
                return await this.postService.developmentPercent(percentDto,request);
            }
        } catch (error) {
            return errorHandler(SingleResponseModel, error);
        }
    } */

}